<template>
    <div class="max-w-5xl mx-auto p-4">
        <h1 class="text-2xl font-semibold text-theme-base mb-2">API Keys</h1>
        <p class="text-sm text-theme-muted mb-4">
            Crea y administra las api keys que usan tus integraciones externas
            (ERP, CRM, fulfillment) para autenticarse contra tu tenant.
        </p>

        <div
            class="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4"
        >
            <div class="flex flex-col gap-2">
                <div v-if="tenantId">
                    <p class="text-sm text-theme-muted mb-1">
                        Id del negocio (header X-Tenant-Id)
                    </p>
                    <uuid-display :value="tenantId" full />
                </div>
                <a
                    :href="docsUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-sm font-medium text-theme-primary hover:underline"
                >
                    Documentación del API ↗
                </a>
            </div>

            <div
                v-if="$ability.hasPermission('apiKey:Create')"
                class="flex flex-col items-start md:items-end gap-2"
            >
                <app-button
                    v-if="canCreateMore"
                    color="success"
                    @click="showCreateDialog = true"
                >
                    Nueva api key
                </app-button>
                <template v-else>
                    <p class="text-sm text-theme-muted md:text-right max-w-sm">
                        {{ limitMessage }}
                    </p>
                    <app-button
                        v-if="canChangePlan"
                        color="brand-blue"
                        @click="openApiKeysUpgrade"
                    >
                        Ver planes
                    </app-button>
                </template>
            </div>
        </div>

        <div class="w-full">
            <custom-sortable-table
                :fields="fields"
                :items="items"
                :per-page="items.length || 20"
                responsive
                hover
                class="w-full"
                items-empty-message="No tienes api keys todavía. Crea una para conectar un sistema externo."
            >
                <template #cell(keyPrefix)="data">
                    <span class="font-mono text-sm">
                        {{ data.item.keyPrefix }}…
                    </span>
                </template>

                <template #cell(permissions)="data">
                    <api-key-permissions-summary
                        :permissions="data.item.permissions || []"
                        :groups="permissionGroups"
                    />
                </template>

                <template #cell(createdByUserId)="data">
                    <div
                        v-if="data.item.createdByUserId"
                        class="flex items-center gap-2"
                    >
                        <router-link
                            v-if="$ability.hasPermission('user:Get', true)"
                            :to="`/app/users/detail/${data.item.createdByUserId}`"
                            class="text-theme-primary"
                            title="Ver detalle"
                        >
                            <chevron-right-icon class="w-4 h-4" />
                        </router-link>
                        <uuid-display :value="data.item.createdByUserId" />
                    </div>
                    <span v-else>-</span>
                </template>

                <template #cell(createdAt)="data">
                    {{ data.item.createdAt | date }}
                </template>

                <template #cell(expiresAt)="data">
                    <span v-if="data.item.expiresAt">
                        {{ data.item.expiresAt | date }}
                    </span>
                    <span v-else class="text-theme-muted">
                        Sin vencimiento
                    </span>
                </template>

                <template #cell(lastUsedAt)="data">
                    <span v-if="data.item.lastUsedAt">
                        {{ data.item.lastUsedAt | date }}
                    </span>
                    <span v-else class="text-theme-muted">Nunca</span>
                </template>

                <template #cell(status)="data">
                    <span
                        class="rounded-full px-2 py-0.5 text-xs font-semibold"
                        :class="statusMeta(data.item).class"
                    >
                        {{ statusMeta(data.item).label }}
                    </span>
                </template>

                <template #cell(actions)="data">
                    <app-button
                        v-if="
                            !data.item.revokedAt &&
                            $ability.hasPermission('apiKey:Revoke')
                        "
                        size="small"
                        border
                        @click="confirmRevoke(data.item)"
                    >
                        Revocar
                    </app-button>
                    <span v-else class="text-theme-muted text-sm">—</span>
                </template>
            </custom-sortable-table>
        </div>

        <api-key-form-dialog v-model="showCreateDialog" @created="loadItems" />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import { MARKETPLACE_API_URL } from "@/common/constants";
import {
    API_KEYS_FIXED_LIMIT,
    API_KEYS_RESOURCE_CODE,
    apiDocsUrl
} from "@/common/fields/api-key-permissions";
import { OperationsModes } from "@/common/fields/operations-modes";
import { planLimitOf } from "@/common/fields/plan-features";

import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import CustomSortableTable from "@/components/utils/CustomSortableTable.vue";
import UuidDisplay from "@/components/utils/UuidDisplay.vue";

import ApiKeyFormDialog from "./ApiKeyFormDialog.vue";
import ApiKeyPermissionsSummary from "./ApiKeyPermissionsSummary.vue";

const STATUS_META = {
    active: {
        label: "Activa",
        class: "bg-theme-success text-theme-inverse"
    },
    expired: {
        label: "Vencida",
        class: "bg-theme-warning text-theme-inverse"
    },
    revoked: {
        label: "Revocada",
        class: "bg-theme-muted text-theme-inverse"
    }
};

export default {
    name: "SettingsApiKeysPage",
    components: {
        ApiKeyFormDialog,
        ApiKeyPermissionsSummary,
        ChevronRightIcon,
        CustomSortableTable,
        UuidDisplay
    },
    layout: "app",
    data: () => ({
        items: [],
        permissionGroups: [],
        showCreateDialog: false,
        fields: [
            { key: "name", label: "Nombre", sortable: true },
            { key: "keyPrefix", label: "Prefijo", sortable: false },
            { key: "permissions", label: "Permisos", sortable: false },
            { key: "createdByUserId", label: "Creada por", sortable: false },
            { key: "createdAt", label: "Creada", sortable: true },
            { key: "expiresAt", label: "Vence", sortable: false },
            { key: "lastUsedAt", label: "Último uso", sortable: false },
            { key: "status", label: "Estado", sortable: false },
            { key: "actions", label: "", sortable: false }
        ]
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        ...mapGetters("platformSubscriptions", ["upgradePlanForResource"]),
        ...mapState("auth", ["subscription", "tenantId"]),
        ...mapState("platformSubscriptions", ["planCatalog"]),
        isMultiTenant() {
            return this.$operationMode === OperationsModes.multiTenant;
        },
        docsUrl() {
            return apiDocsUrl(this.$operationMode, MARKETPLACE_API_URL);
        },
        activeApiKeysCount() {
            return this.items.filter((item) => !item.revokedAt).length;
        },
        isPlanLimited() {
            return this.isMultiTenant && Boolean(this.subscription);
        },
        // Tope de keys activas: 5 fijo fuera de multi-tenant; en
        // multi-tenant el entitlement `apiKeys` del plan de la sesión (a esta
        // pantalla solo llegan Pro y Premium; los planes sin API ven la
        // tarjeta con candado en settings). null = el portal no bloquea (sin
        // tope, o sin datos: valida el backend).
        apiKeysLimit() {
            if (!this.isMultiTenant) return API_KEYS_FIXED_LIMIT;
            if (!this.subscription) return null;
            const plan = this.planCatalog.find(
                (item) => item.code === this.subscription.planCode
            );
            const limit = plan
                ? planLimitOf(plan, API_KEYS_RESOURCE_CODE)
                : null;
            return limit > 0 ? limit : null;
        },
        canCreateMore() {
            return (
                this.apiKeysLimit === null ||
                this.activeApiKeysCount < this.apiKeysLimit
            );
        },
        upgradePlan() {
            if (!this.isPlanLimited) return null;
            return this.upgradePlanForResource(
                API_KEYS_RESOURCE_CODE,
                this.activeApiKeysCount
            );
        },
        canChangePlan() {
            return (
                this.isPlanLimited &&
                this.$ability.hasPermission(
                    "platformSubscription:ChangePlanMine"
                )
            );
        },
        limitMessage() {
            if (!this.isPlanLimited) {
                return `Llegaste al límite de ${API_KEYS_FIXED_LIMIT} api keys activas. Revoca una para crear otra.`;
            }
            const contactOwner = this.canChangePlan
                ? ""
                : " Pídele al dueño de la cuenta que cambie de plan.";
            return `Llegaste al tope de ${this.apiKeysLimit} api keys activas de tu plan. Revoca una o sube de plan.${contactOwner}`;
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("apiKey:List", "/app/settings")))
            return;
        await Promise.all([this.loadItems(), this.loadPlanLimit()]);
    },
    methods: {
        ...mapActions("apiKeys", [
            "ensureApiKeyPermissionCatalog",
            "listApiKeys",
            "revokeApiKey"
        ]),
        ...mapActions("platformSubscriptions", [
            "ensurePlanCatalog",
            "openPlanUpgrade"
        ]),
        // El catálogo de permisos solo da nombres y orden a la lista: si
        // falla, la lista se muestra igual agrupada por prefijo.
        async loadPermissionGroups() {
            try {
                this.permissionGroups =
                    await this.ensureApiKeyPermissionCatalog();
            } catch {
                this.permissionGroups = [];
            }
        },
        // Sin catálogo de planes el portal no bloquea; el backend valida.
        async loadPlanLimit() {
            if (!this.isPlanLimited) return;
            try {
                await this.ensurePlanCatalog();
            } catch {
                // apiKeysLimit queda en null
            }
        },
        openApiKeysUpgrade() {
            this.openPlanUpgrade({
                kind: "limit",
                resourceCode: API_KEYS_RESOURCE_CODE,
                limit: this.apiKeysLimit,
                used: this.activeApiKeysCount,
                upgradePlan: this.upgradePlan
            });
        },
        async loadItems() {
            this.$loading.show();
            try {
                const [items] = await Promise.all([
                    this.listApiKeys(),
                    this.loadPermissionGroups()
                ]);
                this.items = items;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        statusOf(item) {
            if (item.revokedAt) return "revoked";
            if (item.expiresAt && new Date(item.expiresAt) < new Date())
                return "expired";
            return "active";
        },
        statusMeta(item) {
            return STATUS_META[this.statusOf(item)];
        },
        async confirmRevoke(item) {
            const result = await this.$swal.fire({
                background: this.backgroundColor,
                title: "¿Revocar api key?",
                text: `Se revocará "${item.name}" y dejará de funcionar de inmediato.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Revocar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#e53e3e"
            });

            if (!result.isConfirmed) return;

            try {
                await this.revokeApiKey({ id: item.id });
                await this.loadItems();
            } catch (error) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
